/**
 * /claim-family/:token  — public page (no auth required).
 *
 * Phase 72. When a coach imports a family via Bulk Import, we email
 * the parent a magic link to this page. They confirm their email,
 * choose a password, accept the legal terms, and we instantly create
 * their user account + link it to the existing parent_profile (which
 * already has their kids attached and an approved application with
 * the coach). They land in the parent dashboard signed in.
 *
 * If the token is unknown / expired / already claimed, we render a
 * friendly error state with a "go to login" CTA instead of a 404.
 */
import React, { useEffect, useState } from 'react';
import Seo from '@/components/Seo';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, CheckCircle2, AlertTriangle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';

const LEGAL_VERSION = '1.0.0';

export default function ClaimFamily() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { refresh, setAuth } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [info, setInfo] = useState(null);
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [accept, setAccept] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(`/auth/claim-family/${token}`);
                if (!cancelled) setInfo(r.data);
            } catch (e) {
                if (!cancelled) setError(e?.response?.data?.detail || 'This link is no longer valid.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    const submit = async () => {
        if (password.length < 10) { toast.error('Password must be at least 10 characters.'); return; }
        if (password !== confirm) { toast.error('Passwords don\'t match.'); return; }
        if (!accept) { toast.error('Please accept the Terms and Privacy Policy.'); return; }
        setSubmitting(true);
        try {
            const r = await api.post(`/auth/claim-family/${token}`, {
                password,
                legal_accepted_version: LEGAL_VERSION,
            });
            const jwt = r.data?.token;
            if (jwt) {
                // Phase 118 — the backend now sets the httpOnly session cookie;
                // setAuth() only caches non-sensitive user metadata.
                setAuth?.(jwt, { email: r.data?.email, role: 'parent' });
                await refresh?.();
                toast.success('Welcome! Your account is ready.');
                navigate('/parent/dashboard');
            } else {
                toast.success('Account claimed. Please sign in.');
                navigate('/login');
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not claim your account. Try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex min-h-dvh items-center justify-center bg-[#f6fafd]">
                <Loader2 className="h-6 w-6 animate-spin text-[#00d4ff]" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex min-h-dvh items-center justify-center bg-[#f6fafd] p-6">
                <Card className="max-w-md p-8 text-center" data-testid="claim-family-error">
                    <AlertTriangle className="mx-auto h-10 w-10 text-[#ff6b35]" />
                    <h1 className="mt-3 font-display text-xl font-bold text-[#0a1628]">Invitation link not valid</h1>
                    <p className="mt-2 text-sm text-[#1a3a5c]">{error}</p>
                    <div className="mt-5">
                        <Button asChild className="sd-coral-cta"><Link to="/login">Go to sign in</Link></Button>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="flex min-h-dvh items-center justify-center bg-[#f6fafd] p-6">
            <Seo
                title="Claim your family account | SplashPad"
                description="Finish setting up your SplashPad family account to manage swim lessons and payments."
                canonical="https://splashpad.pro/claim"
                noIndex
            />
            <Card className="w-full max-w-md p-8" data-testid="claim-family-form">
                <div className="mb-4 flex items-center gap-3">
                    {info?.coach?.logo_url ? (
                        <img src={info.coach.logo_url} alt="" className="h-10 w-10 rounded-full object-cover" />
                    ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#00d4ff]/15">
                            <ShieldCheck className="h-5 w-5 text-[#00d4ff]" />
                        </div>
                    )}
                    <div className="min-w-0">
                        <div className="text-xs uppercase tracking-wide text-[#1a3a5c]/60">SplashPad</div>
                        <div className="font-semibold text-[#0a1628]" data-testid="claim-family-coach">
                            {info?.coach?.display_name || 'Your coach'} set up your account
                        </div>
                    </div>
                </div>
                <p className="text-sm text-[#1a3a5c]">
                    Pick a password to claim your SplashPad account. Your kids and schedule are already linked — you&apos;ll go straight to your dashboard.
                </p>
                <div className="mt-5 space-y-3">
                    <div>
                        <Label className="text-xs">Email</Label>
                        <Input value={info?.email || ''} disabled data-testid="claim-family-email" />
                    </div>
                    <div>
                        <Label className="text-xs">Choose a password (10+ characters)</Label>
                        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="claim-family-password" />
                    </div>
                    <div>
                        <Label className="text-xs">Confirm password</Label>
                        <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="claim-family-confirm" />
                    </div>
                    <label className="flex items-start gap-2 pt-1 text-xs text-[#1a3a5c]">
                        <Checkbox checked={accept} onCheckedChange={(v) => setAccept(!!v)} data-testid="claim-family-accept" />
                        <span>
                            I agree to the <Link to="/legal/terms" target="_blank" className="font-semibold underline">Terms of Service</Link> and <Link to="/legal/privacy" target="_blank" className="font-semibold underline">Privacy Policy</Link>.
                        </span>
                    </label>
                </div>
                <Button onClick={submit} disabled={submitting} className="sd-coral-cta sd-focus mt-5 w-full" data-testid="claim-family-submit">
                    {submitting ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Setting up your account…</> : <><CheckCircle2 className="mr-1 h-4 w-4" /> Claim my account</>}
                </Button>
            </Card>
        </div>
    );
}
